import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { setDivision } from "../../Redux/Action/division";
import OrderTakerNavbarAllPAges from "../../Components/OrderTakerNavbarAllPAges";
import Loading from "../../Components/Loading";

const BILLMERGING = ({ hideNavBar }) => {
  const BASEURL = process.env.REACT_APP_BASEURL;
  const BASEURL_GLOBAL = process.env.REACT_APP_BASEURL_GLOBAL;

  const authToken = window.sessionStorage.getItem("auth-token");
  const sessiontoken = window.sessionStorage.getItem("session-token");

  const dispatch = useDispatch();

  const divisionState = useSelector((state) => state.changeDivision);
  const userState = useSelector((state) => state.changeTheState.user);

  const loggedInEmail = userState?.emailId;
  const loggedInUser = userState?.emailId?.split("@")[0]?.toUpperCase();

  // ============================================================
  // CONFIGURATION
  // ============================================================

  // Replace these with the actual SP IDs when provided.
  const MERGE_BILLS_SP_ID = "XXXX";
  const TRANSFER_ITEM_SP_ID = "XXXX";

  // Menu ID supplied in the specification.
  const RESAVE_MENU_ID = 887;

  // ============================================================
  // COMMON STATE
  // ============================================================

  const [activeAction, setActiveAction] = useState("");

  const [search, setSearch] = useState("");

  const [startdate, setStartdate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );

  const [enddate, setEnddate] = useState(
    moment().format("YYYY-MM-DD")
  );

  const [orderHeaders, setOrderHeaders] = useState([]);

  const [isloading, setIsloading] = useState(false);

  const [selectedOrderNos, setSelectedOrderNos] = useState([]);

  // ============================================================
  // BILL LEVEL MERGE
  // ============================================================

  const [mergeAllocations, setMergeAllocations] = useState({});

  const [isMergePopupOpen, setIsMergePopupOpen] = useState(false);

  // ============================================================
  // MOVE / TRANSFER
  // ============================================================

  const [isTransferPopupOpen, setIsTransferPopupOpen] = useState(false);

  const [transferOrderNo, setTransferOrderNo] = useState("");

  const [selectedTransferItems, setSelectedTransferItems] = useState([]);

  const [transferItems, setTransferItems] = useState([]);

  const [transferLoading, setTransferLoading] = useState(false);

  const [newBillNo, setNewBillNo] = useState("");

  // ============================================================
  // RESAVE MENU 887
  // ============================================================

  const [showResaveMenu, setShowResaveMenu] = useState(false);

  const [resaveOrderNo, setResaveOrderNo] = useState("");

  const [resaveQueue, setResaveQueue] = useState([]);

  // ============================================================
  // HEADERS
  // ============================================================

  const getHeaders = () => ({
    headers: {
      "auth-token": authToken,
      "session-token": sessiontoken,
    },
  });

  // ============================================================
  // FETCH ORDER LIST - OBJECT ID 1680
  // ============================================================

  const getOrderHeaders = async () => {
    try {
      setIsloading(true);

      // Division rights
      try {
        const divisionResponse = await axios.get(
          `${BASEURL}/getdivisionRights?OM_USER_ACCOUNT=${loggedInEmail}`
        );

        dispatch(
          setDivision(
            divisionResponse?.data?.[0]?.OM_USER_DIVISION || ""
          )
        );
      } catch (divisionError) {
        console.log(
          "Error fetching division rights:",
          divisionError
        );
      }

      const response = await axios.get(
        `${BASEURL_GLOBAL}/globalViewHandler?viewname=1680&WAITER=${encodeURIComponent(
          loggedInUser || ""
        )}`,
        getHeaders()
      );

      const data = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];

      const sortedData = [...data].sort(
        (a, b) => Number(b?.BILLNO || 0) - Number(a?.BILLNO || 0)
      );

      setOrderHeaders(sortedData);
    } catch (error) {
      console.error(
        "Error while fetching Object ID 1680:",
        error
      );

      setOrderHeaders([]);
    } finally {
      setIsloading(false);
    }
  };

  useEffect(() => {
    getOrderHeaders();
  }, []);

  // ============================================================
  // COMMON FILTER
  // ============================================================

  const filteredOrders = useMemo(() => {
    const searchTerms = search
      .toLowerCase()
      .split(" ")
      .filter(Boolean);

    return orderHeaders.filter((item) => {
      const textMatch =
        searchTerms.length === 0 ||
        searchTerms.every((term) =>
          [
            "BILLNO",
            "TABLENO",
            "DIVISION",
            "CURRENCY",
            "WAITER",
            "RECEIPTAMOUNT",
            "AMOUNT",
          ].some((key) =>
            String(item?.[key] ?? "")
              .toLowerCase()
              .includes(term)
          )
        );

      const itemDate = item?.DATETIME
        ? moment(item.DATETIME)
        : null;

      const start = startdate
        ? moment(startdate).startOf("day")
        : null;

      const end = enddate
        ? moment(enddate).endOf("day")
        : null;

      const dateMatch =
        (!start || (itemDate && itemDate.isSameOrAfter(start))) &&
        (!end || (itemDate && itemDate.isSameOrBefore(end)));

      return textMatch && dateMatch;
    });
  }, [orderHeaders, search, startdate, enddate]);

  // ============================================================
  // COMMON ORDER TABLE CHECKBOX
  // ============================================================

  const toggleOrderSelection = (billNo) => {
    const bill = String(billNo);

    setSelectedOrderNos((prev) => {
      if (prev.includes(bill)) {
        return prev.filter((item) => item !== bill);
      }

      return [...prev, bill];
    });
  };

  const toggleAllOrders = () => {
    const visibleBillNos = filteredOrders.map((row) =>
      String(row?.BILLNO)
    );

    const allSelected = visibleBillNos.every((billNo) =>
      selectedOrderNos.includes(billNo)
    );

    if (allSelected) {
      setSelectedOrderNos((prev) =>
        prev.filter((billNo) => !visibleBillNos.includes(billNo))
      );
    } else {
      setSelectedOrderNos((prev) => [
        ...new Set([...prev, ...visibleBillNos]),
      ]);
    }
  };

  // ============================================================
  // OPEN BILL LEVEL MERGE
  // ============================================================

  const openBillLevelMerge = () => {
    setActiveAction("MERGE");

    setSelectedOrderNos([]);

    setMergeAllocations({});

    setIsMergePopupOpen(true);
  };

  // ============================================================
  // ALLOCATED AMOUNT
  // ============================================================

  const handleAllocationChange = (billNo, value) => {
    // Allow only numbers and decimal
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setMergeAllocations((prev) => ({
      ...prev,
      [billNo]: value,
    }));
  };

  // ============================================================
  // MERGE BILLS
  // ============================================================

  const handleMergeBills = async () => {
    if (selectedOrderNos.length < 2) {
      alert("Kindly Select At Least Two Bills");
      return;
    }

    const selectedBills = filteredOrders.filter((row) =>
      selectedOrderNos.includes(String(row?.BILLNO))
    );

    const payload = {
      WAITER: loggedInUser,
      BILLNOS: selectedBills.map((row) => ({
        BILLNO: row?.BILLNO,
        ALLOCATEDAMOUNT:
          mergeAllocations?.[String(row?.BILLNO)] || "",
      })),
    };

    console.log("Merge Bills Payload:", payload);

    try {
      if (MERGE_BILLS_SP_ID === "XXXX") {
        console.log(
          "MERGE_BILLS_SP_ID is not configured.",
          payload
        );

        alert(
          "Merge SP ID is not configured. Check the console for the payload."
        );

        return;
      }

      const response = await axios.post(
        `${BASEURL_GLOBAL}/globalSpHandler?spname=${MERGE_BILLS_SP_ID}`,
        payload,
        getHeaders()
      );

      console.log("Merge Bill Response:", response?.data);

      alert("Bills Merged Successfully");

      setSelectedOrderNos([]);
      setMergeAllocations({});
      setIsMergePopupOpen(false);

      await getOrderHeaders();
    } catch (error) {
      console.error("Merge Bills Error:", error);

      alert("Unable to Merge Bills");
    }
  };

  // ============================================================
  // OPEN MOVE / TRANSFER
  // ============================================================

  const openMoveTransfer = () => {
    setActiveAction("TRANSFER");

    setSearch("");

    setSelectedOrderNos([]);

    setTransferOrderNo("");

    setTransferItems([]);

    setSelectedTransferItems([]);

    setNewBillNo("");

    setIsTransferPopupOpen(true);
  };

  // ============================================================
  // FETCH ITEMS FROM OBJECT ID 622
  // ============================================================

  const fetchTransferItems = async (billNo) => {
    if (!billNo) return;

    try {
      setTransferLoading(true);

      const response = await axios.get(
        `${BASEURL_GLOBAL}/globalViewHandler?viewname=622&INVNO=${encodeURIComponent(
          billNo
        )}`,
        getHeaders()
      );

      console.log(
        "Transfer Item Object 622 Response:",
        response?.data
      );

      const data = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];

      const formattedItems = data.map((item, index) => ({
        ...item,
        _rowId: `${billNo}-${index}`,
      }));

      setTransferItems(formattedItems);

      setSelectedTransferItems([]);

      // ========================================================
      // NEW BILL NUMBERS
      // Must be distinct from the selected order.
      // ========================================================

      const distinctBillNumbers = [
        ...new Set(
          orderHeaders
            .map((item) => String(item?.BILLNO))
            .filter(
              (bill) =>
                bill &&
                bill !== String(billNo)
            )
        ),
      ];

      // Automatically use the first valid destination
      // only if one exists.
      setNewBillNo(distinctBillNumbers?.[0] || "");
    } catch (error) {
      console.error(
        "Error fetching Object ID 622:",
        error
      );

      setTransferItems([]);

      alert("Unable to Fetch Selected Bill Items");
    } finally {
      setTransferLoading(false);
    }
  };

  // ============================================================
  // OPEN PRODUCT WINDOW
  // ============================================================

  const handleTransferOrderClick = async (row) => {
    const billNo = String(row?.BILLNO || "");

    if (!billNo) return;

    setTransferOrderNo(billNo);

    await fetchTransferItems(billNo);
  };

  // ============================================================
  // SELECT / UNSELECT ITEM
  // ============================================================

  const toggleTransferItem = (rowId) => {
    setSelectedTransferItems((prev) => {
      if (prev.includes(rowId)) {
        return prev.filter((id) => id !== rowId);
      }

      return [...prev, rowId];
    });
  };

  const toggleAllTransferItems = () => {
    const rowIds = transferItems.map((item) => item._rowId);

    const allSelected =
      rowIds.length > 0 &&
      rowIds.every((id) =>
        selectedTransferItems.includes(id)
      );

    if (allSelected) {
      setSelectedTransferItems([]);
    } else {
      setSelectedTransferItems(rowIds);
    }
  };

  // ============================================================
  // DESTINATION BILL LIST
  // ============================================================

  const destinationBills = useMemo(() => {
    return [
      ...new Set(
        orderHeaders
          .map((row) => String(row?.BILLNO))
          .filter(
            (billNo) =>
              billNo &&
              billNo !== String(transferOrderNo)
          )
      ),
    ];
  }, [orderHeaders, transferOrderNo]);

  // ============================================================
  // TRANSFER ITEMS
  // ============================================================

  const handleTransferItems = async () => {
    if (!transferOrderNo) {
      alert("Kindly Select an Order");
      return;
    }

    if (selectedTransferItems.length === 0) {
      alert("Kindly Select At Least One Item");
      return;
    }

    if (!newBillNo) {
      alert("Kindly Select New Bill No");
      return;
    }

    if (String(newBillNo) === String(transferOrderNo)) {
      alert("New Bill No Cannot Be Same as Selected Bill");
      return;
    }

    const itemsToTransfer = transferItems
      .filter((item) =>
        selectedTransferItems.includes(item._rowId)
      )
      .map((item) => ({
        ITEMCODE: item?.["ITEM CODE"],
        ITEMNAME: item?.["ITEM NAME"],
        QTY: item?.QTY,
        ITEMVALUEEXCVAT: item?.["ITEM VALUE EXC VAT"],
        VATAMOUNT: item?.["VAT AMOUNT"],
        LEVYAMOUNT: item?.["LEVY AMOUNT"],
        RATEINCLUSIVE: item?.["RATE INCLUSIVE"],
        ITEMVALUE: item?.["ITEM VALUE"],
      }));

    const payload = {
      FROMBILLNO: transferOrderNo,
      TOBILLNO: newBillNo,
      WAITER: loggedInUser,
      ITEMS: itemsToTransfer,
    };

    console.log(
      "Transfer Item Payload:",
      payload
    );

    try {
      if (TRANSFER_ITEM_SP_ID === "XXXX") {
        console.log(
          "TRANSFER_ITEM_SP_ID is not configured.",
          payload
        );

        alert(
          "Transfer SP ID is not configured. Check the console for the payload."
        );

        return;
      }

      setTransferLoading(true);

      const response = await axios.post(
        `${BASEURL_GLOBAL}/globalSpHandler?spname=${TRANSFER_ITEM_SP_ID}`,
        payload,
        getHeaders()
      );

      console.log(
        "Transfer Item Response:",
        response?.data
      );

      alert("Item(s) Transferred Successfully");

      // ========================================================
      // Requirement:
      //
      // First open Menu 887 for the original bill.
      // Then open Menu 887 for the destination bill.
      // ========================================================

      const queue = [
        String(transferOrderNo),
        String(newBillNo),
      ];

      setResaveQueue(queue);

      setResaveOrderNo(queue[0]);

      setShowResaveMenu(true);

      // Refresh original order list
      await getOrderHeaders();
    } catch (error) {
      console.error(
        "Transfer Item Error:",
        error
      );

      alert("Unable to Transfer Item(s)");
    } finally {
      setTransferLoading(false);
    }
  };

  // ============================================================
  // MENU 887 - RESAVE
  // ============================================================

  const handleResaveComplete = () => {
    const remainingBills = resaveQueue.filter(
      (billNo) => String(billNo) !== String(resaveOrderNo)
    );

    if (remainingBills.length > 0) {
      const nextBill = remainingBills[0];

      setResaveQueue(remainingBills);

      setResaveOrderNo(nextBill);

      setShowResaveMenu(true);
    } else {
      setResaveQueue([]);

      setResaveOrderNo("");

      setShowResaveMenu(false);

      alert("Both Orders Resaved Successfully");
    }
  };

  // ============================================================
  // CLOSE POPUPS
  // ============================================================

  const closeMergePopup = () => {
    setIsMergePopupOpen(false);

    setSelectedOrderNos([]);

    setMergeAllocations({});
  };

  const closeTransferPopup = () => {
    setIsTransferPopupOpen(false);

    setTransferOrderNo("");

    setTransferItems([]);

    setSelectedTransferItems([]);

    setNewBillNo("");
  };

  // ============================================================
  // FORMAT AMOUNT
  // ============================================================

  const formatAmount = (value) => {
    return Number(value || 0).toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (isloading) {
    return <Loading />;
  }

  // ============================================================
  // MAIN UI
  // ============================================================

  return (
    <div className="bg-gray-50 min-h-screen">

      {!hideNavBar && divisionState && <OrderTakerNavbarAllPAges />}

      {/* ======================================================
          MAIN ACTION BUTTONS
      ======================================================= */}

      <div className="px-5 pt-5">

        <div className="flex flex-wrap gap-4">

          <button
            type="button"
            onClick={openBillLevelMerge}
            className="border border-black bg-[#365f9c] text-white px-6 py-2 rounded-sm hover:bg-[#294c7d]"
          >
            Bill Level Merge
          </button>

          <button
            type="button"
            onClick={openMoveTransfer}
            className="border border-black bg-[#365f9c] text-white px-6 py-2 rounded-sm hover:bg-[#294c7d]"
          >
            Move/Transfer Item
          </button>

        </div>

      </div>

      {/* ======================================================
          BILL LEVEL MERGE POPUP
      ======================================================= */}

      {isMergePopupOpen && (
        <div className="fixed top-[70px] left-1/2 -translate-x-1/2 bg-white border border-black lg:w-[1200px] w-[95%] h-[calc(100vh-90px)] p-3 overflow-y-auto z-[500] rounded-md shadow-xl">

          <button
            type="button"
            className="absolute top-[15px] right-2 border border-black bg-[#002d62] text-white rounded-md px-4 py-1"
            onClick={closeMergePopup}
          >
            Close
          </button>

          <h2 className="text-xl font-bold underline mb-4">
            Bill Level Merge
          </h2>

          {/* Search / Dates */}

          <div className="flex flex-wrap items-center gap-4 mb-4">

            <div className="flex items-center gap-2">
              <label>Search:</label>

              <input
                type="text"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                className="w-[220px] h-8 border border-black rounded-md px-2"
              />
            </div>

            <div className="flex items-center gap-2">

              <label className="text-red-600">
                Start Date:
              </label>

              <input
                type="date"
                value={startdate}
                onChange={(e) =>
                  setStartdate(e.target.value)
                }
                className="w-36 h-8 border border-black rounded-md px-2"
              />

            </div>

            <div className="flex items-center gap-2">

              <label className="text-red-600">
                End Date:
              </label>

              <input
                type="date"
                value={enddate}
                onChange={(e) =>
                  setEnddate(e.target.value)
                }
                className="w-36 h-8 border border-black rounded-md px-2"
              />

            </div>

          </div>

          {/* Merge Grid */}

          <div className="overflow-x-auto">

            <table className="custom-table w-auto text-[10px]">

              <thead className="header-cell">

                <tr>

                  <td className="text-center">
                    S NO
                  </td>

                  <td className="text-center">
                    SELECT
                  </td>

                  <td>DIVISION</td>

                  <td>ORDER NO</td>

                  <td>TABLE NO</td>

                  <td>WAITER</td>

                  <td>ORDER TIME</td>

                  <td className="text-right">
                    AMOUNT
                  </td>

                  <td className="text-right">
                    RECEIPT AMOUNT
                  </td>

                  <td className="text-right">
                    ALLOCATED AMOUNT
                  </td>

                  <td className="text-right">
                    PENDING
                  </td>

                  <td>CURRENCY</td>

                </tr>

              </thead>

              <tbody>

                {filteredOrders.length > 0 ? (

                  filteredOrders.map((row, index) => {

                    const billNo = String(
                      row?.BILLNO
                    );

                    const isSelected =
                      selectedOrderNos.includes(
                        billNo
                      );

                    const amount =
                      Number(row?.AMOUNT || 0);

                    const receiptAmount =
                      Number(
                        row?.RECEIPTAMOUNT || 0
                      );

                    const allocatedAmount =
                      Number(
                        mergeAllocations?.[billNo] ||
                          0
                      );

                    const pending =
                      Math.max(
                        amount -
                          receiptAmount -
                          allocatedAmount,
                        0
                      );

                    return (
                      <tr key={billNo}>

                        <td className="text-center">
                          {index + 1}
                        </td>

                        <td className="text-center">

                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              toggleOrderSelection(
                                billNo
                              )
                            }
                          />

                        </td>

                        <td>
                          {row?.DIVISION || ""}
                        </td>

                        <td className="text-right">
                          {row?.BILLNO || ""}
                        </td>

                        <td>
                          {row?.TABLENO || ""}
                        </td>

                        <td>
                          {row?.WAITER || ""}
                        </td>

                        <td>
                          {row?.DATETIME
                            ? moment
                                .utc(row.DATETIME)
                                .format(
                                  "DD-MM-YYYY HH:mm:ss"
                                )
                            : ""}
                        </td>

                        <td className="text-right">
                          {formatAmount(amount)}
                        </td>

                        <td className="text-right">
                          {formatAmount(
                            receiptAmount
                          )}
                        </td>

                        <td className="text-right">

                          <input
                            type="text"
                            value={
                              mergeAllocations?.[
                                billNo
                              ] || ""
                            }
                            disabled={!isSelected}
                            onChange={(e) =>
                              handleAllocationChange(
                                billNo,
                                e.target.value
                              )
                            }
                            className="w-[100px] h-7 border border-black text-right px-1 rounded-sm"
                          />

                        </td>

                        <td className="text-right">
                          {formatAmount(pending)}
                        </td>

                        <td>
                          {row?.CURRENCY || ""}
                        </td>

                      </tr>
                    );
                  })

                ) : (

                  <tr>
                    <td
                      colSpan={12}
                      className="text-center py-5"
                    >
                      No Orders Available
                    </td>
                  </tr>

                )}

              </tbody>

            </table>

          </div>

          {/* Merge Button */}

          <div className="mt-5">

            <button
              type="button"
              onClick={handleMergeBills}
              className="border border-black bg-[#365f9c] text-white px-6 py-2 rounded-sm hover:bg-[#294c7d]"
            >
              Merge Bills Selected
            </button>

          </div>

        </div>
      )}

      {/* ======================================================
          MOVE / TRANSFER ORDER LIST
      ======================================================= */}

      {isTransferPopupOpen && (
        <div className="fixed top-[70px] left-1/2 -translate-x-1/2 bg-white border border-black lg:w-[1200px] w-[95%] h-[calc(100vh-90px)] p-3 overflow-y-auto z-[500] rounded-md shadow-xl">

          <button
            type="button"
            className="absolute top-[15px] right-2 border border-black bg-[#002d62] text-white rounded-md px-4 py-1"
            onClick={closeTransferPopup}
          >
            Close
          </button>

          <h2 className="text-xl font-bold underline mb-4">
            Move / Transfer Item
          </h2>

          {/* Search / Dates */}

          <div className="flex flex-wrap items-center gap-4 mb-4">

            <div className="flex items-center gap-2">
              <label>Search:</label>

              <input
                type="text"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                className="w-[220px] h-8 border border-black rounded-md px-2"
              />
            </div>

            <div className="flex items-center gap-2">

              <label className="text-red-600">
                Start Date:
              </label>

              <input
                type="date"
                value={startdate}
                onChange={(e) =>
                  setStartdate(e.target.value)
                }
                className="w-36 h-8 border border-black rounded-md px-2"
              />

            </div>

            <div className="flex items-center gap-2">

              <label className="text-red-600">
                End Date:
              </label>

              <input
                type="date"
                value={enddate}
                onChange={(e) =>
                  setEnddate(e.target.value)
                }
                className="w-36 h-8 border border-black rounded-md px-2"
              />

            </div>

          </div>

          {/* Order Grid */}

          <div className="overflow-x-auto">

            <table className="custom-table w-auto text-[10px]">

              <thead className="header-cell">

                <tr>

                  <td className="text-center">
                    S NO
                  </td>

                  <td>DIVISION</td>

                  <td>ORDER NO</td>

                  <td>TABLE NO</td>

                  <td>WAITER</td>

                  <td>ORDER TIME</td>

                  <td className="text-right">
                    AMOUNT
                  </td>

                  <td className="text-right">
                    RECEIPT AMOUNT
                  </td>

                  <td>CURRENCY</td>

                </tr>

              </thead>

              <tbody>

                {filteredOrders.length > 0 ? (

                  filteredOrders.map((row, index) => (

                    <tr
                      key={row?.BILLNO}
                      className="border-b cursor-pointer text-[10px] table-row hover:bg-gray-100"
                      onClick={() =>
                        handleTransferOrderClick(row)
                      }
                    >

                      <td className="text-center">
                        {index + 1}
                      </td>

                      <td>
                        {row?.DIVISION || ""}
                      </td>

                      <td className="text-right">
                        {row?.BILLNO || ""}
                      </td>

                      <td>
                        {row?.TABLENO || ""}
                      </td>

                      <td>
                        {row?.WAITER || ""}
                      </td>

                      <td>
                        {row?.DATETIME
                          ? moment
                              .utc(row.DATETIME)
                              .format(
                                "DD-MM-YYYY HH:mm:ss"
                              )
                          : ""}
                      </td>

                      <td className="text-right">
                        {formatAmount(
                          row?.AMOUNT
                        )}
                      </td>

                      <td className="text-right">
                        {formatAmount(
                          row?.RECEIPTAMOUNT
                        )}
                      </td>

                      <td>
                        {row?.CURRENCY || ""}
                      </td>

                    </tr>

                  ))

                ) : (

                  <tr>
                    <td
                      colSpan={9}
                      className="text-center py-5"
                    >
                      No Orders Available
                    </td>
                  </tr>

                )}

              </tbody>

            </table>

          </div>

          {/* ==================================================
              SELECTED ORDER ITEM GRID
          =================================================== */}

          {transferOrderNo && (

            <div className="mt-6 border-t border-gray-300 pt-4">

              <div className="flex flex-wrap items-center justify-between mb-3">

                <h3 className="text-lg font-bold underline">
                  Items from Order No:{" "}
                  {transferOrderNo}
                </h3>

                <div className="flex items-center gap-2">

                  <label className="font-semibold">
                    New Bill No:
                  </label>

                  <select
                    value={newBillNo}
                    onChange={(e) =>
                      setNewBillNo(e.target.value)
                    }
                    className="border border-black h-8 px-2 min-w-[150px]"
                  >

                    <option value="">
                      Select Bill
                    </option>

                    {destinationBills.map(
                      (billNo) => (
                        <option
                          key={billNo}
                          value={billNo}
                        >
                          {billNo}
                        </option>
                      )
                    )}

                  </select>

                </div>

              </div>

              {transferLoading ? (

                <div className="text-center py-8 font-semibold">
                  Loading...
                </div>

              ) : (

                <>

                  <div className="overflow-x-auto">

                    <table className="custom-table w-auto text-[10px]">

                      <thead className="header-cell">

                        <tr>

                          <td className="text-center">
                            SNO
                          </td>

                          {/* <td className="text-center">

                            <input
                              type="checkbox"
                              checked={
                                transferItems.length >
                                  0 &&
                                selectedTransferItems.length ===
                                  transferItems.length
                              }
                              onChange={
                                toggleAllTransferItems
                              }
                            />

                          </td> */}

                          <td>
                            Product Code
                          </td>

                          <td>
                            Product Name
                          </td>

                          <td className="text-right">
                            Quantity
                          </td>

                          <td className="text-right">
                            Rate (Exclusive)
                          </td>

                          <td className="text-right">
                            VAT Amount
                          </td>

                          <td className="text-right">
                            Levy Amount
                          </td>

                          <td className="text-right">
                            Rate (Inclusive)
                          </td>

                          <td className="text-right">
                            Total Amount
                          </td>

                          <td className="text-center">
                            Select
                          </td>

                        </tr>

                      </thead>

                      <tbody>

                        {transferItems.length > 0 ? (

                          transferItems.map(
                            (item, index) => {

                              const selected =
                                selectedTransferItems.includes(
                                  item._rowId
                                );

                              return (
                                <tr
                                  key={item._rowId}
                                  className={
                                    selected
                                      ? "bg-gray-100"
                                      : ""
                                  }
                                >

                                  <td className="text-center">
                                    {index + 1}
                                  </td>

                                  {/* <td className="text-center">

                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={() =>
                                        toggleTransferItem(
                                          item._rowId
                                        )
                                      }
                                    />

                                  </td> */}

                                  <td>
                                    {item?.[
                                      "ITEM CODE"
                                    ] || ""}
                                  </td>

                                  <td>
                                    {item?.[
                                      "ITEM NAME"
                                    ] || ""}
                                  </td>

                                  <td className="text-right">
                                    {Number(
                                      item?.QTY || 0
                                    )}
                                  </td>

                                  <td className="text-right">
                                    {formatAmount(
                                      item?.[
                                        "ITEM VALUE EXC VAT"
                                      ]
                                    )}
                                  </td>

                                  <td className="text-right">
                                    {formatAmount(
                                      item?.[
                                        "VAT AMOUNT"
                                      ]
                                    )}
                                  </td>

                                  <td className="text-right">
                                    {formatAmount(
                                      item?.[
                                        "LEVY AMOUNT"
                                      ]
                                    )}
                                  </td>

                                  <td className="text-right">
                                    {formatAmount(
                                      item?.[
                                        "RATE INCLUSIVE"
                                      ]
                                    )}
                                  </td>

                                  <td className="text-right">
                                    {formatAmount(
                                      item?.[
                                        "ITEM VALUE"
                                      ]
                                    )}
                                  </td>

                                  <td className="text-center">

                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={() =>
                                        toggleTransferItem(
                                          item._rowId
                                        )
                                      }
                                    />

                                  </td>

                                </tr>
                              );
                            }
                          )

                        ) : (

                          <tr>
                            <td
                              colSpan={11}
                              className="text-center py-5"
                            >
                              No Items Available
                            </td>
                          </tr>

                        )}

                      </tbody>

                    </table>

                  </div>

                  <div className="mt-5">

                    <button
                      type="button"
                      disabled={
                        transferLoading ||
                        selectedTransferItems.length ===
                          0
                      }
                      onClick={
                        handleTransferItems
                      }
                      className="border border-black bg-[#365f9c] text-white px-6 py-2 rounded-sm disabled:opacity-50"
                    >
                      Transfer Item to then Selected Bill
                    </button>

                  </div>

                </>

              )}

            </div>
          )}

        </div>
      )}

      {/* ======================================================
          MENU 887 - RESAVE
      ======================================================= */}

      {showResaveMenu && (
        <div className="fixed inset-0 bg-black/40 z-[700] flex items-center justify-center">

          <div className="bg-white border border-black rounded-md shadow-xl w-[500px] p-5">

            <div className="flex justify-between items-center mb-5">

              <h2 className="text-lg font-bold">
                Menu {RESAVE_MENU_ID}
              </h2>

              <button
                type="button"
                className="border border-black bg-[#002d62] text-white px-3 py-1 rounded-md"
                onClick={() => {
                  setShowResaveMenu(false);
                  setResaveQueue([]);
                  setResaveOrderNo("");
                }}
              >
                Close
              </button>

            </div>

            <div className="border border-gray-300 p-4">

              <p className="mb-3">
                Please resave Order No:
              </p>

              <div className="font-bold text-xl mb-5">
                {resaveOrderNo}
              </div>

              <button
                type="button"
                onClick={handleResaveComplete}
                className="border border-black bg-[#365f9c] text-white px-6 py-2 rounded-sm"
              >
                Resave
              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  );
};

export default BILLMERGING;